import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import Pagination from "../../../../../../../components/base/pagination";
import {useUpdateEffect} from "react-use";
import useSearch from "../../../../../../../hooks/use-search";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {SvgIcon, Tooltip} from "@mui/material";
import {Col} from "reactstrap";
import classnames from "classnames";
import DateUtils from "../../../../../../../../core/services/date-utils";
import {ReactComponent as TimeIcon} from "../../../../../../../../assets/images/notifications/time.svg";
import {ReactComponent as DuplicateIcon} from "../../../../../../../../assets/images/notifications/duplicate.svg";

const PrintHubQueuesList = forwardRef(({hubId, onHistorySelected}, ref) => {
    const [queues, setQueues] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Exposes the addQueue function to the parent component.
     */
    useImperativeHandle(ref, () => ({
        addQueue: (queue) => setQueues(prevState => [queue, ...prevState]),
    }), []);

    /**
     * Fetches the print queues from the server.
     * @type {(function(UseSearchFunctionArg): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args) => {
        const forApi = {
            partnerHubId: hubId,
            pagination: args.paginationInfo,
        }
        const response = await Api.getPrinterHubQueues(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setQueues(response.data?.items ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.paginationInfo?.length ?? 0,
            }))
        }
    }, [isMounted, hubId]);

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result, reset = true) => {
        if (reset)
            setQueues([]);
        await fetchEntries(result);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => DefaultPaginationInfo, [])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,

    }), [initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings, false)

    /**
     * With each change in the hub id,
     * - resets the pagination info and fetches the print histories.
     */
    useUpdateEffect(() => {
        searchData.setPaginationInfo(initialPaginationInfo);
        setQueues([]);
        search(searchData, false).then();
    }, [hubId]);

    /**
     * Changes the page of the pagination.
     * @type {(function(*): void)}
     */
    const onPageChange = useCallback((e) => {
        searchData.setPaginationInfo(prevState => ({...prevState, currentPage: e.selected + 1}))
    }, [searchData])

    /**
     * Checks if the given queues are processing and updates the list of histories.
     * @type {(function(*): Promise<void>)}
     */
    const checkIfQueuesProcessing = useCallback(async (printHistories) => {
        const histories = printHistories.filter(e => !e.isDone);
        if (!histories.length)
            return;
        const forApi = {
            queueIds: histories.map(e => e.id),
        };
        const response = await Api.checkStatusOfPrinterHubQueues(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const obj = Object.fromEntries(response.data.map(e => [`${e.queueId}`, e.isDone]))
            const queues = printHistories.map(e => ({...e, isDone: e.isDone || obj[`${e.id}`]}));
            setQueues(prevState => prevState.map(e => queues.find(h => h.id === e.id) || e));
        }
    }, [isMounted]);

    /**
     * With each change in the list of print histories, checks if any of them are processing.
     */
    useEffect(() => {
        const anyProcessing = queues.some(e => !e.isDone);
        if (!anyProcessing)
            return;
        const timer = setTimeout(() => {
            checkIfQueuesProcessing(queues).then();
        }, 10000);
        return () => clearTimeout(timer);
    }, [queues, checkIfQueuesProcessing])

    return (
        <>
            <div className={'print-histories'}>
                <p className={'mini-title'}>
                    Queues
                </p>
                {
                    searchData.loading &&
                    Array(5).fill(0).map((_, index) => (
                        <div key={index} className={'loading normal w-100 mb-3'}>
                            <div/>
                        </div>
                    ))
                }
                {
                    !searchData.loading &&
                    queues.map((queue) => (
                        <PrintHubQueueCard
                            key={queue.id}
                            queue={queue}
                            onQueueSelected={onHistorySelected}
                        />
                    ))
                }
                {
                    !searchData.loading && !queues.length &&
                    <div className={'my-5'}>
                        <p className={'text-center w-100 text-sm'}>
                            No queues found.
                        </p>
                    </div>
                }
                {
                    !searchData.loading && searchData.paginationInfo?.length > searchData.paginationInfo?.pageSize && (
                        <div className='w-100 d-flex justify-content-center align-items-center mb-5 mt-3'>
                            <Pagination
                                currentPage={searchData.paginationInfo?.currentPage}
                                length={searchData.paginationInfo?.length}
                                pageSize={searchData.paginationInfo?.pageSize}
                                onPageChange={onPageChange}
                            />
                        </div>
                    )
                }
            </div>
        </>
    );
})


const PrintHubQueueCard = ({queue, onQueueSelected}) => {

    return (
        <div className={'print-hub-history-card'}>
            <div className={'d-flex align-items-center'}>
                <p className={'title'}>
                    {queue?.name ?? ''}
                </p>
                <IconButton onPressed={() => onQueueSelected(queue)} color={'secondary'}>
                    <SvgIcon component={DuplicateIcon} className={'icon normal'}/>
                </IconButton>
            </div>
            <div className={'mt-2'}>
                <p className={'bold'}>
                    Operator:
                    <span>{queue?.operatorName ?? ''}</span>
                </p>
            </div>
            <div className={'d-flex align-items-center justify-content-between'}>
                <Col xs={6} className={'p-0 d-flex align-items-center'}>
                    <p className={'bold mb-0'}>
                        Printer:
                        <span>{queue?.printerName ?? ''}</span>
                    </p>
                </Col>
                <Col xs={6} className={'text-right p-0'}>
                    <p className={'bold'}>
                        <span>{queue?.fileName ?? ''}</span>
                    </p>
                </Col>
            </div>
            <div className={'d-flex align-items-center justify-content-between'}>
                <Col xs={'auto'} className={'p-0 d-flex align-items-center'}>
                    <p className={'bold mb-0'}>
                        Status:
                    </p>
                    <Tooltip title={!queue.isDone ? "Processing" : "Finished"}>
                        <div
                            className={classnames('status-ball', {'finished': queue.isDone})}
                        />
                    </Tooltip>
                </Col>
                <Col xs className={'text-right p-0'}>
                    <p className={'date'}>
                        <TimeIcon className={'mx-1'}/>
                        {
                            queue?.submittedDateTime
                                ? DateUtils.serverToLocal(queue.submittedDateTime).format(DateUtils.dateTimeFormat)
                                : ''
                        }
                    </p>
                </Col>
            </div>
        </div>
    );
}


export default PrintHubQueuesList;
